import React from 'react';

const TypingRaceSlide = ({
    slide,
    slideKey,
    currentSlide,
    slidesLength,
    raceStarted,
    setRaceStarted,
    humanText,
    aiText,
    humanWordCount,
    aiWordCount,
    raceTimer,
    raceFinished,
    aiFinishTime
}) => {
    const totalWords = slide.raceText?.split(' ').length || 50;

    return (
        /* `safe center` au lieu de `justify-center` : quand le contenu dépasse
           la hauteur — ce qui arrive une fois la course lancée — un centrage
           classique répartit le débordement des DEUX côtés, et le titre passe
           sous le bandeau de chapitre. En mode `safe`, le centrage cède et le
           contenu s'aligne en haut plutôt que de sortir par le haut. Sans
           effet tant que la place suffit. */
        <div key={slideKey} className="h-full p-12 flex flex-col [justify-content:safe_center] relative">
            <div className="relative z-10">
                <h2 className="text-6xl pb-2 font-bold mb-4 bg-gradient-to-r from-blue-400 via-cyan-400 to-blue-400 bg-clip-text text-transparent animate-fade-in-up text-center">
                    {slide.title}
                </h2>
                <p className="text-2xl text-blue-200/60 mb-4 animate-fade-in-up stagger-1 text-center">
                    {slide.subtitle}
                </p>

                {/* Champ optionnel : repère chiffré discret, affiché avant comme
                    pendant la course. Absent d'une slide, rien ne change. */}
                {slide.note && (
                    <p className="text-lg text-blue-300/50 font-mono mb-6 animate-fade-in-up stagger-2 text-center">
                        {slide.note}
                    </p>
                )}

                {!raceStarted ? (
                    <div className="flex flex-col items-center gap-8 animate-fade-in-up stagger-2">
                        <div className="text-center max-w-2xl">
                            <p className="text-xl text-slate-300 mb-4">
                                Comparez la vitesse d'écriture entre un humain et une IA.
                            </p>
                            <div className="flex justify-center gap-12 mb-8">
                                <div className="text-center">
                                    <div className="text-6xl mb-2">🧑</div>
                                    <div className="text-3xl font-bold text-orange-400">1 mot/sec</div>
                                    <div className="text-base text-slate-400">Humain moyen</div>
                                </div>
                                <div className="text-4xl text-slate-500 self-center">VS</div>
                                <div className="text-center">
                                    <div className="text-6xl mb-2">🤖</div>
                                    <div className="text-3xl font-bold text-cyan-400">20 à 80 mots/sec</div>
                                    <div className="text-base text-slate-400">GPT-4 / Claude</div>
                                </div>
                            </div>
                        </div>
                        <button
                            onClick={() => setRaceStarted(true)}
                            className="px-12 py-4 bg-gradient-to-r from-cyan-500 to-blue-600 text-white text-xl font-bold rounded-xl hover:scale-105 transition-all shadow-[0_0_30px_rgba(34,211,238,0.3)] hover:shadow-[0_0_50px_rgba(34,211,238,0.5)]"
                        >
                            🏁 Lancer la Course
                        </button>
                    </div>
                ) : (
                    <div className="space-y-6">
                        {/* Timer central */}
                        <div className="flex justify-center mb-4">
                            <div className="px-6 py-2 bg-slate-800/80 border border-blue-500/30 rounded-full">
                                <span className="text-3xl font-mono text-blue-300">⏱️ {raceTimer}s</span>
                            </div>
                        </div>
                        {/* Comparaison finale */}
                        {raceFinished && (
                            <div className="mt-8 animate-fade-in-up">
                                <div className="max-w-2xl mx-auto bg-gradient-to-r from-cyan-500/10 to-purple-500/10 border border-cyan-500/30 rounded-2xl p-6 text-center">
                                    <p className="text-3xl text-white font-bold mb-2">
                                        🏆 L'IA a terminé en <span className="text-cyan-400">{aiFinishTime || raceTimer} seconde{(aiFinishTime || raceTimer) > 1 ? 's' : ''}</span>
                                    </p>
                                    <p className="text-2xl text-slate-300">
                                        Un humain aura besoin de plus de <span className="text-orange-400 font-bold">{totalWords} secondes</span> pour le même texte
                                    </p>
                                    <p className="text-xl text-slate-400 mt-4">
                                        Ratio de vitesse : <span className="text-cyan-400 font-bold">×{Math.round(totalWords / Math.max(1, aiFinishTime || raceTimer))}</span> plus rapide
                                    </p>
                                </div>
                            </div>
                        )}
                        <div className="grid grid-cols-2 gap-8 max-w-6xl mx-auto">
                            {/* Colonne Humain */}
                            <div className="space-y-4">
                                <div className="text-center">
                                    <span className="inline-flex items-center gap-2 px-6 py-3 bg-orange-500/20 border border-orange-500/40 rounded-full text-orange-300 font-bold text-2xl">
                                        🧑 HUMAIN
                                        <span className="text-base font-normal">(1 mot/sec)</span>
                                    </span>
                                </div>
                                <div className="flex justify-between items-center px-4">
                                    <div className="flex items-center gap-2">
                                        <span className="text-orange-400 font-bold text-2xl">{humanWordCount}</span>
                                        <span className="text-slate-400">mots</span>
                                    </div>
                                    <div className="h-2 flex-1 mx-4 bg-slate-700 rounded-full overflow-hidden">
                                        <div
                                            className="h-full bg-gradient-to-r from-orange-500 to-orange-400 transition-all duration-300"
                                            style={{ width: `${(humanWordCount / totalWords) * 100}%` }}
                                        />
                                    </div>
                                </div>
                                <div className="bg-slate-900/60 border border-orange-500/20 rounded-xl p-6 h-[280px] max-h-[32vh] relative overflow-y-auto custom-scrollbar">
                                    <p className="text-slate-300 font-mono text-xl leading-relaxed">
                                        {humanText}
                                        {!raceFinished && <span className="typing-cursor"></span>}
                                    </p>
                                    {humanWordCount === 0 && (
                                        <div className="absolute inset-0 flex items-center justify-center">
                                            <span className="text-slate-500 text-lg">En attente...</span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Colonne IA */}
                            <div className="space-y-4">
                                <div className="text-center">
                                    <span className="inline-flex items-center gap-2 px-6 py-3 bg-cyan-500/20 border border-cyan-500/40 rounded-full text-cyan-300 font-bold text-2xl">
                                        🤖 IA
                                        <span className="text-base font-normal">(20 mots/sec)</span>
                                    </span>
                                </div>
                                <div className="flex justify-between items-center px-4">
                                    <div className="flex items-center gap-2">
                                        <span className="text-cyan-400 font-bold text-2xl">{aiWordCount}</span>
                                        <span className="text-slate-400">mots</span>
                                    </div>
                                    <div className="h-2 flex-1 mx-4 bg-slate-700 rounded-full overflow-hidden">
                                        <div
                                            className="h-full bg-gradient-to-r from-cyan-500 to-blue-400 transition-all duration-100"
                                            style={{ width: `${(aiWordCount / totalWords) * 100}%` }}
                                        />
                                    </div>
                                    {aiWordCount > 0 && (
                                        <div className="text-green-400 font-bold animate-pulse">✓ TERMINÉ</div>
                                    )}
                                </div>
                                <div className={`bg-slate-900/60 border rounded-xl p-6 h-[280px] max-h-[32vh] relative overflow-y-auto custom-scrollbar transition-all duration-500 ${aiText ? 'border-cyan-500/40 shadow-[0_0_20px_rgba(34,211,238,0.2)]' : 'border-cyan-500/20'}`}>
                                    <p className={`text-slate-300 font-mono text-xl leading-relaxed transition-all duration-100 ${aiText ? 'animate-fade-in' : ''}`}>
                                        {aiText}
                                    </p>
                                    {!aiText && (
                                        <div className="absolute inset-0 flex items-center justify-center">
                                            <span className="text-slate-500 text-lg">Prêt...</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default TypingRaceSlide;
